import React from "react";
import PropTypes from "prop-types";

import { PrimaryButton } from "../../../components/ui";
import LoaderComponent from "../../../components/loader/Loader.component";
import { SEDE_ANALYTICS_ACCESS } from "./hooks/useSedeAnalyticsAccess";
import {
  LoaderContainer,
  SedeNotice,
  SedeNoticeTitle,
  SedeNoticeDescription,
} from "./AnalyticsView.styles";

/**
 * What a sede tab shows while the analytics service has not answered whether
 * the advanced dashboard can be used.
 */
export const SedeAnalyticsChecking = () => (
  <LoaderContainer role="status" aria-label="Comprobando las analíticas avanzadas">
    <LoaderComponent width={50} height={50} />
  </LoaderContainer>
);

/**
 * Wraps the advanced analytics dashboard and draws what `useSedeAnalyticsAccess`
 * answered. It asks nothing itself: the screen calls the hook once and hands
 * the answer down, so a sede tab makes one check per visit.
 *
 * Outside sede mode the access is available from the first render and the
 * gate is transparent: the dashboard renders on the first paint, as always.
 *
 * Partner access: in a sede tab the dashboard only mounts once the analytics
 * service answered. Until then a loader shows, and while the service does not
 * answer one notice replaces the whole dashboard, so none of its widgets
 * fires a request that is going to fail.
 *
 * A sede user without any widget permission gets the dashboard as anywhere
 * else: it makes no guarded call for them and shows its own "no permission"
 * texts.
 */
const SedeAnalyticsGate = ({ access, onGoToClassic, children }) => {
  if (access === SEDE_ANALYTICS_ACCESS.CHECKING) return <SedeAnalyticsChecking />;

  if (access === SEDE_ANALYTICS_ACCESS.UNAVAILABLE) {
    return (
      <SedeNotice data-testid="sede-analytics-unavailable">
        <SedeNoticeTitle>
          Las analíticas avanzadas todavía no están disponibles dentro de una sede.
        </SedeNoticeTitle>
        <SedeNoticeDescription>
          Mientras tanto puedes consultar las estadísticas clásicas de esta sede.
        </SedeNoticeDescription>
        <PrimaryButton type="button" onClick={onGoToClassic}>
          Ver estadísticas clásicas
        </PrimaryButton>
      </SedeNotice>
    );
  }

  return children;
};

SedeAnalyticsGate.propTypes = {
  access: PropTypes.oneOf(Object.values(SEDE_ANALYTICS_ACCESS)).isRequired,
  onGoToClassic: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export default SedeAnalyticsGate;
